
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MenuManagement from './components/MenuManagement';
import ReservationManagement from './components/ReservationManagement';
import HeroManagement from './components/HeroManagement';
import AboutManagement from './components/AboutManagement';
import AdminLogin from './components/AdminLogin';

const AdminPage: FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigate = useNavigate();

  const tabs = [
    { id: 'hero', name: '홈 슬라이드', icon: 'ri-image-line' },
    { id: 'about', name: '소개 관리', icon: 'ri-information-line' },
    { id: 'menu', name: '메뉴 관리', icon: 'ri-restaurant-line' },
    { id: 'reservation', name: '예약 관리', icon: 'ri-calendar-line' }
  ];

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    if (hasUnsavedChanges) {
      if (!confirm('저장하지 않은 변경사항이 있습니다. 정말 로그아웃하시겠습니까?')) {
        return;
      }
    }
    setIsLoggedIn(false);
    setHasUnsavedChanges(false);
    navigate('/');
  };

  const handleSaveAll = async () => {
    try {
      // 현재 활성 탭에 따라 저장 로직 실행
      const saveEvent = new CustomEvent('adminSave', { 
        detail: { tab: activeTab } 
      });
      window.dispatchEvent(saveEvent);
      
      setHasUnsavedChanges(false);
      
      // 성공 메시지 표시
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      notification.innerHTML = `
        <i class="ri-check-line mr-2"></i>
        모든 변경사항이 저장되었습니다.
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.remove();
      }, 3000);
      
    } catch (error) {
      console.error('저장 실패:', error);
      
      // 오류 메시지 표시
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center';
      notification.innerHTML = `
        <i class="ri-error-warning-line mr-2"></i>
        저장 중 오류가 발생했습니다.
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.remove();
      }, 3000);
    }
  };

  const handleTabChange = (tabId: string) => {
    if (hasUnsavedChanges) {
      if (!confirm('저장하지 않은 변경사항이 있습니다. 탭을 변경하시겠습니까?')) {
        return;
      }
      setHasUnsavedChanges(false);
    }
    setActiveTab(tabId);
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-4">관리자 페이지</h1>
              <p className="text-gray-600">레스토랑 정보를 관리하세요</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSaveAll}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center"
              >
                <i className="ri-save-line mr-2"></i>
                저장
              </button>
              <button
                onClick={handleLogout}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2"></i>
                로그아웃
              </button>
            </div>
          </div>

          {/* 변경사항 알림 */}
          {hasUnsavedChanges && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center">
              <i className="ri-information-line text-yellow-600 mr-3"></i>
              <span className="text-yellow-800">저장하지 않은 변경사항이 있습니다.</span>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm mb-8">
            <div className="flex border-b">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? 'border-b-2 border-red-500 text-red-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={`${tab.icon} mr-2`}></i>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            {activeTab === 'hero' && <HeroManagement />}
            {activeTab === 'about' && <AboutManagement />}
            {activeTab === 'menu' && <MenuManagement />}
            {activeTab === 'reservation' && <ReservationManagement />}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminPage;
